import { useEffect, useState } from "react";
import { getAmbassadorInvitationActionLink } from "../../../../apis/invitations.api";

const ACTOR_LABELS: Record<string, string> = {
  ambassador: "Ambassador",
  vendor: "Client",
};

const actionLinkCache = new Map<string, InvitationActionLinkData | null>();

export interface InvitationActionLinkData {
  link: string;
  action?: string;
  actor: string;
}

export interface InvitationActionLinkState {
  linkData: InvitationActionLinkData | null;
  loading: boolean;
}

export function getActionLinkActorLabel(actor: string) {
  return ACTOR_LABELS[actor] ?? actor;
}

function getActionLinkCacheKey(
  ambassadorInvitationId: string,
  refreshKey?: string
) {
  return `${ambassadorInvitationId}:${refreshKey ?? ""}`;
}

export function useInvitationActionLink(
  ambassadorInvitationId?: string,
  refreshKey?: string,
  enabled = false
): InvitationActionLinkState {
  const [linkData, setLinkData] = useState<InvitationActionLinkData | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !ambassadorInvitationId) {
      setLoading(false);
      return;
    }

    const cacheKey = getActionLinkCacheKey(
      ambassadorInvitationId,
      refreshKey
    );

    if (actionLinkCache.has(cacheKey)) {
      setLinkData(actionLinkCache.get(cacheKey) ?? null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLinkData(null);

    getAmbassadorInvitationActionLink(ambassadorInvitationId)
      .then((res) => {
        if (cancelled) return;

        setLoading(false);

        const nextLinkData =
          res?.success && res?.data?.link
            ? {
                link: res.data.link,
                action: res.data.action,
                actor: res.data.actor,
              }
            : null;

        actionLinkCache.set(cacheKey, nextLinkData);
        setLinkData(nextLinkData);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
          setLinkData(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ambassadorInvitationId, refreshKey, enabled]);

  return { linkData, loading };
}
